import type { AnalyticsFlightsAndAppSettings, AnalyticsFlightsAndEcsSettingsAddon, } from "owa-analytics-types";
import type { AccountKey } from "owa-client-types";
import { getAnalyticsAddon } from "owa-analytics-addons";
import { trace } from "owa-trace";
import { getCachedSettings, resolveAnalyticsCacheKey } from "./getCachedSettings";
const flightsAndApplicationSettingsCacheByAccountKey = new Map<string, AnalyticsFlightsAndAppSettings>();

export function getAnalyticsFlightsAndAppSettings(accountKey: AccountKey | "globalAccount" = "globalAccount"): AnalyticsFlightsAndAppSettings | null {
    /**
     * When running tests, we won't pull the actual flights so we need to pass some default ones instead.
     * This will be removed in prod builds.
     */
    if (process.env.JEST_WORKER_ID) {
        return TEST_ONLY_getDefaultSettings();
    }
    
    // On the main thread, use the local cache if available for speed
    const cacheKey = resolveAnalyticsCacheKey(accountKey, "getAnalyticsFlightsAndAppSettings");
    const cachedFlights = flightsAndApplicationSettingsCacheByAccountKey.get(cacheKey);
    if (cachedFlights) {
        return cachedFlights;
    }
    
    // On the workers, use the cached settings if available
    const cachedSettings = getCachedSettings(accountKey);
    if (cachedSettings?.flightsAndEcsSettings) {
        return cachedSettings?.flightsAndEcsSettings;
    }
    
    // If the settings don't exist, fallback to trying to initialize them (only works on the main thread)
    const analyticsFlightsAndEcsSettingsAddon = getAnalyticsAddon<AnalyticsFlightsAndEcsSettingsAddon>("AnalyticsFlightsAndEcsSettings");
    
    if (analyticsFlightsAndEcsSettingsAddon.isRegistered) {
        const flights = analyticsFlightsAndEcsSettingsAddon
            .executeNow()
            ?.initializeAnalyticsFlightsAndAppSettings(accountKey);
        
        if (flights) {
            flightsAndApplicationSettingsCacheByAccountKey.set(cacheKey, flights);
        }
        return flights ?? null;
    }
    
    trace.warn(`AnalyticsFlightsAndEcsSettingsAddon is not registered. This is expected during worker initialization ` +
        `as worker threads rely on synced cache from the main thread. AccountKey: ${accountKey}. CacheKey: ${cacheKey}`, "analytics");
    return null;
}

export function updateFlightsAndApplicationSettingsCache(cache: AnalyticsFlightsAndAppSettings, accountKey: AccountKey | "globalAccount" = "globalAccount"): void {
    const cacheKey = resolveAnalyticsCacheKey(accountKey, "updateFlightsAndApplicationSettingsCache");
    flightsAndApplicationSettingsCacheByAccountKey.set(cacheKey, cache);
}

function TEST_ONLY_getDefaultSettings(): AnalyticsFlightsAndAppSettings {
    return {
        disabledDatapoints: [],
        throttledDatapoints: [],
        disableIsCoreDatapoints: [],
        enableHighCountEventThrottle: true,
        highCountEventThrottleWindow: 15,
        highCountEventThrottleThreshold: 500,
        capturePerfProfiles: [],
        maxPerfProfilesPerSession: 25,
        flightControls: {},
        
        // Feature Flights
        shouldCaptureAssets: true,
        skipFlightControls: false,
        isFullVerboseLoggingEnabled: false,
        enablePrivacyGuard: false,
        enableSchemaEnforcement: false,
        turnOffCustomData: false,
        devToolsEnabled: false,
        trackEventFrequency: false,
        isDataWorkerEnabled: false,
        enableOfflineCaching: false,
        isDiagDatapointTracingPanelEnabled: false,
    };
}
